import Chart from 'react-apexcharts';
import { type ApexOptions } from 'apexcharts';
import { useMemo } from 'react';

interface ChartProps {
  type: 'line' | 'bar' | 'pie' | 'donut' | 'area' | 'radialBar';
  series: any[];
  options?: ApexOptions;
  height?: number;
  className?: string;
}

export default function ApexChart({
  type,
  series,
  options = {},
  height = 350,
  className = ''
}: ChartProps) {

  // Detect chart type for global use
  const isPieChart = type === 'pie' || type === 'donut' || type === 'radialBar'

  // Comprehensive data sanitization for ApexCharts
  const { validSeries, sanitizedOptions } = useMemo(() => {
    // Handle case where series is not an array at all
    if (!Array.isArray(series)) {
      return {
        validSeries: [],
        sanitizedOptions: { ...options }
      }
    }

    // Detect chart type and required data format
    const seriesTypes = series.map(s => s?.type).filter(Boolean)
    const hasMixedTypes = seriesTypes.length > 1 || seriesTypes.includes('line')
    const needsObjectFormat = hasMixedTypes || type === 'line'

    // Sanitize options first - needed for series processing
    const sanitizedOptions = { ...options }
    if (sanitizedOptions.xaxis?.categories) {
      sanitizedOptions.xaxis.categories = Array.isArray(sanitizedOptions.xaxis.categories)
        ? sanitizedOptions.xaxis.categories.map(cat =>
            typeof cat === 'string' ? cat : String(cat || '')
          )
        : []
    }

    // Sanitize colors array
    if (sanitizedOptions.colors) {
      sanitizedOptions.colors = Array.isArray(sanitizedOptions.colors)
        ? sanitizedOptions.colors.filter(color => typeof color === 'string')
        : undefined
    }

    // Sanitize labels for pie/donut charts
    if (sanitizedOptions.labels) {
      sanitizedOptions.labels = Array.isArray(sanitizedOptions.labels)
        ? sanitizedOptions.labels.map(label =>
            typeof label === 'string' ? label : String(label || '')
          )
        : []
    }

    // Handle radialBar and pie charts differently - they expect simple numeric arrays
    if (isPieChart) {
      // For pie/radialBar charts, series should be a simple numeric array
      const validSeries = Array.isArray(series) ? series.filter(s =>
        typeof s === 'number' && !isNaN(s) && isFinite(s)
      ) : []

      return { validSeries, sanitizedOptions }
    }

    // Sanitize series data for other chart types
    const validSeries = series.map((s, index) => {
      // Handle cases where series contains primitive values instead of objects
      if (typeof s === 'number') {
        return { name: `Series ${index + 1}`, data: [s] }
      }

      if (typeof s === 'string') {
        return { name: s, data: [] }
      }

      if (!s || typeof s !== 'object') {
        return { name: `Unknown ${index + 1}`, data: [] }
      }

      const sanitizedSeries = {
        name: typeof s.name === 'string' ? s.name : 'Unknown',
        type: typeof s.type === 'string' ? s.type : undefined,
        data: Array.isArray(s.data) ? s.data.map((d, dataIndex) => {
          // Handle line/bar/mixed charts
          if (typeof d === 'number' && !isNaN(d) && isFinite(d)) {
            // Use object format for mixed charts or when categories are provided
            if (needsObjectFormat || (sanitizedOptions.xaxis?.categories && sanitizedOptions.xaxis.categories.length > 0)) {
              return { x: sanitizedOptions.xaxis?.categories?.[dataIndex] || dataIndex, y: d }
            }
            return d
          }

          if (typeof d === 'object' && d !== null) {
            if (typeof d.y === 'number' && !isNaN(d.y) && isFinite(d.y)) {
              return {
                x: d.x !== undefined ? d.x : (sanitizedOptions.xaxis?.categories?.[dataIndex] || dataIndex),
                y: d.y
              }
            }
            if (typeof d.x !== 'undefined' && typeof d.y === 'number') {
              return { x: d.x, y: isNaN(d.y) ? 0 : d.y }
            }
          }

          // Return consistent format based on chart type
          if (needsObjectFormat) {
            return { x: sanitizedOptions.xaxis?.categories?.[dataIndex] || dataIndex, y: 0 }
          }
          return 0
        }) : []
      }

      // Remove undefined properties
      if (sanitizedSeries.type === undefined) {
        delete sanitizedSeries.type
      }

      return sanitizedSeries
    })

    return { validSeries, sanitizedOptions }
  }, [series, options, isPieChart, type])

  // Detect combined bar+line charts for data labels
  const hasCombinedBarLine = useMemo(() => {
    if (!Array.isArray(validSeries)) return false

    const types = validSeries
      .map(s => s?.type)
      .filter(t => t === 'column' || t === 'line')

    return types.includes('column') && types.includes('line')
  }, [validSeries])

  // Get indices of line series for data labels
  const lineSeriesIndices = useMemo(() => {
    if (!Array.isArray(validSeries)) return []

    return validSeries
      .map((s, idx) => s?.type === 'line' ? idx : -1)
      .filter(idx => idx >= 0)
  }, [validSeries])

  // Merge with default options
  const finalOptions: ApexOptions = useMemo(() => {
    // Base configuration
    const baseConfig: ApexOptions = {
      chart: {
        toolbar: { show: false },
        zoom: { enabled: false },
        ...sanitizedOptions.chart,
      },
      colors: sanitizedOptions.colors || ['#2F4062', '#6CACE4', '#3BA55D', '#E53935', '#F9A825', '#256EF4'],
      theme: {
        palette: 'palette1',
        ...sanitizedOptions.theme,
      },
      grid: {
        borderColor: '#EBEBEB',
        ...sanitizedOptions.grid,
      },
    }

    // Add dataLabels configuration for combined bar+line charts
    const dataLabelsConfig = hasCombinedBarLine && lineSeriesIndices.length > 0 ? {
      dataLabels: {
        enabled: true,
        enabledOnSeries: lineSeriesIndices,
        formatter: (value: number) => {
          // Format based on value magnitude (assume percentage if 0-100)
          if (value >= 0 && value <= 100) {
            return `${value.toFixed(1)}%`
          }
          return `${Math.round(value).toLocaleString()}`
        },
        style: {
          fontSize: '11px',
          fontFamily: 'Pretendard GOV, Pretendard, system-ui, sans-serif',
          fontWeight: '400',
          colors: ['#6E6E6E']
        },
        background: {
          enabled: true,
          foreColor: '#FFFFFF',
          borderRadius: 2,
          padding: 4,
          opacity: 0.9,
          borderWidth: 1,
          borderColor: '#D1D1D1'
        },
        offsetY: -6,
        dropShadow: {
          enabled: false
        },
        ...sanitizedOptions.dataLabels
      }
    } : {}

    return {
      ...baseConfig,
      ...dataLabelsConfig,
      ...sanitizedOptions,
    }
  }, [sanitizedOptions, hasCombinedBarLine, lineSeriesIndices])

  // Don't render if no valid data
  const hasValidData = () => {
    if (!validSeries.length) return false

    // For pie/radialBar charts, check if we have numeric values (allow 0)
    if (isPieChart) {
      return validSeries.some(s => typeof s === 'number' && s >= 0)
    }

    // For other charts, check if series have data
    return validSeries.some(s => s && s.data && s.data.length > 0)
  }

  // Error boundary for ApexCharts with React 19 compatibility
  // Use stable key based on data content instead of timestamp
  const stableKey = useMemo(() => {
    // Create a stable key based on the actual data content
    const seriesKey = JSON.stringify(validSeries).slice(0, 50) // Use first 50 chars of series data
    const typeKey = type
    return `chart-${typeKey}-${seriesKey}`
  }, [validSeries, type])

  if (!hasValidData()) {
    return (
      <div className={className}>
        <div style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#999'
        }}>
          데이터가 없습니다
        </div>
      </div>
    )
  }

  try {
    return (
      <div className={className}>
        <Chart
          options={finalOptions}
          series={validSeries}
          type={type}
          height={height}
          key={stableKey} // Use stable key based on data content
        />
      </div>
    );
  } catch (error) {
    console.error('ApexCharts rendering error:', error)
    console.error('Error details:', {
      type,
      series: validSeries,
      options: finalOptions
    })
    return (
      <div className={className}>
        <div style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#ff0000',
          border: '1px solid #ff0000',
          borderRadius: '4px'
        }}>
          차트 렌더링 중 오류가 발생했습니다.
        </div>
      </div>
    )
  }
}